/**
 * Event in a Box — Expand invitation radius (migrations 2026-10-13-460..464).
 *
 * Thin client over the server RPCs. Every decision (who is new, who is excluded, when it goes out, which
 * follow-ups survive) is made on the server; this file only carries the operator's choices and shows results.
 */
import { supabase } from "./supabase";

export interface ExpansionBlocker {
  code: string;
  message: string;
}

export interface ExpansionMeasured {
  launch_id: string;
  wave_number: number;
  max_mi: number | null;
  p95_mi: number | null;
  robust_mi: number | null;
  outlier: boolean;
  n: number;
  n_no_coords: number;
}

export interface ExpansionWave {
  launch_id: string;
  wave_number: number;
  qualifies: boolean;
  rule_radius_mi: number | null;
  count: number | null;
  flight_status: string | null;
  send_at: string | null;
  measured: ExpansionMeasured | null;
}

export interface ExpansionOffset {
  kind: "reminder" | "resend";
  offset_days: number;
  due_at: string | null;
  kept: boolean;
  why: string | null;
}

export interface ExpansionProposal {
  attempt_no: number;
  ring_candidates: number;
  excluded: Record<string, number>;
  cohort: number;
  never_mailed: {
    total: number;
    about_reachable: number;
    included: number;
    toggle: boolean;
  };
  withheld_at_commit: number;
  quality: {
    cohort: number;
    will_reach_estimate: number;
    no_email: number;
    suppressed: number;
    invalid: number;
    never_evaluated: number;
  };
  bands: {
    inside_source: number;
    covered_by_custom_wave: number | null;
    new_ring: number;
    no_coordinates: number;
    source_radius_mi: number;
    middle_edge_mi: number | null;
    new_radius_mi: number;
  };
  cumulative_total: number;
  send: {
    send_at: string;
    branch: "0800" | "next_hour" | "next_hour_quiet";
    days_ahead: number;
    tz: string;
    send_at_local: string;
  };
  offsets: ExpansionOffset[];
  kept_followups: number;
  template_id: string;
  contract: {
    workflow_mode: string;
    guest_award_enabled: boolean;
    expected_capacity: number | null;
    checkin_acknowledged: boolean;
  };
  in_flight: Array<{
    launch_id: string;
    wave_number: number;
    size: number;
    flight_status: string;
    send_at: string | null;
    excluded_here: number;
  }>;
  measured_coverage: ExpansionMeasured[];
  rule_covered_mi: number;
  headline: { widest_mi: number; headline_mi: number };
  coverage_warning: boolean;
  registration: { registered: number; cap: number | null };
  source: { cadence_wave: number; source_radius_mi: number };
  typed_total_required: boolean;
  venue_changed: boolean;
}

export interface ExpansionOutcomes {
  sent: number;
  skipped_at_send: number;
  withheld: number;
  omitted: number;
  pending: number;
  unexplained: number;
}

export interface ExpansionRequest {
  id: string;
  status:
    | "building"
    | "previewing"
    | "ready"
    | "confirmed"
    | "committed"
    | "cancelled"
    | "failed";
  attempt_no: number;
  created_by: string;
  created_by_name: string | null;
  is_mine: boolean;
  new_radius_mi: number;
  inside_radius_mi: number | null;
  source_radius_mi: number;
  rule_covered_mi: number;
  include_never_mailed: boolean;
  proposal: ExpansionProposal | null;
  proposal_digest: string | null;
  confirmed_proposal_digest: string | null;
  last_refresh_reason: string | null;
  failure_code: string | null;
  failure_message: string | null;
  result:
    | (Record<string, unknown> & {
        expansion_number?: number;
        audience_count?: number;
        send_at?: string;
        outcome?: string;
      })
    | null;
  expansion_launch_id: string | null;
  confirmed_at: string | null;
  created_at: string;
  template_id: string | null;
  flight_status: string | null;
  flight_id: string | null;
  scheduled_send_at: string | null;
  voided: boolean;
  outcomes: ExpansionOutcomes | null;
}

export interface ExpansionStatus {
  enabled: boolean;
  actor_ok: boolean;
  can_expand: boolean;
  blockers: ExpansionBlocker[];
  source: {
    criteria_source_launch_id: string | null;
    cadence_source_launch_id: string | null;
    source_radius_mi: number | null;
    anchor_kind: string | null;
  };
  rule_covered_mi: number | null;
  headline: { widest_mi: number; headline_mi: number };
  waves: ExpansionWave[];
  expansions: Array<{
    request_id: string;
    new_radius_mi: number;
    launch_id: string;
    expansion_number: number | null;
  }>;
  requests: ExpansionRequest[];
  live_request_id: string | null;
}

/** Server error text without the machine code prefix, for the operator. */
export function expansionErrorText(e: unknown): string {
  const raw =
    (e as { message?: string })?.message ??
    (typeof e === "string" ? e : "Something went wrong.");
  return raw.replace(/^EIB_[A-Z0-9_]+:\s*/, "");
}

async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) {
    throw error;
  }
  return data as T;
}

export function getExpansionStatus(eventId: string): Promise<ExpansionStatus> {
  return call<ExpansionStatus>("eib_expansion_status", { p_event: eventId });
}

export function prepareExpansion(params: {
  eventId: string;
  idempotencyKey: string;
  newRadius: number;
  insideRadius: number | null;
  includeNeverMailed: boolean;
}): Promise<ExpansionRequest> {
  return call<ExpansionRequest>("eib_expansion_prepare", {
    p_event: params.eventId,
    p_idempotency_key: params.idempotencyKey,
    p_new_radius: params.newRadius,
    p_inside_radius: params.insideRadius,
    p_include_never_mailed: params.includeNeverMailed,
  });
}

export function confirmExpansion(params: {
  requestId: string;
  proposalDigest: string;
  typedTotal: number | null;
  ackNoFollowups: boolean;
  ackVenueChange: boolean;
  ackCheckin: boolean;
  ackRadiusWithinCoverage: boolean;
}): Promise<ExpansionRequest & { launch_id?: string; replayed?: boolean }> {
  return call("eib_expansion_confirm", {
    p_request: params.requestId,
    p_proposal_digest: params.proposalDigest,
    p_typed_total: params.typedTotal,
    p_ack_no_followups: params.ackNoFollowups,
    p_ack_venue_change: params.ackVenueChange,
    p_ack_checkin: params.ackCheckin,
    p_ack_radius_within_coverage: params.ackRadiusWithinCoverage,
  });
}

export function cancelExpansion(requestId: string): Promise<ExpansionRequest> {
  return call<ExpansionRequest>("eib_expansion_cancel", {
    p_request: requestId,
  });
}

/**
 * The invitation design check (same edge function the wizard uses). The commit only accepts a check
 * recorded after the latest confirm, so this runs right after confirming.
 */
export async function verifyExpansionDesign(
  launchId: string,
  templateId: string,
): Promise<void> {
  const { data, error } = await supabase.functions.invoke(
    "eib-validate-blueprint",
    {
      body: { launch_id: launchId, template_id: templateId },
    },
  );
  if (error) {
    let detail = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.text === "function") {
      try {
        const parsed = JSON.parse(await ctx.text()) as { error?: string };
        if (parsed?.error) {
          detail = parsed.error;
        }
      } catch {
        /* keep the generic message */
      }
    }
    throw new Error(`The invitation design could not be verified: ${detail}`);
  }
  const body = data as { success?: boolean; error?: string } | null;
  if (!body?.success) {
    throw new Error(
      `The invitation design could not be verified: ${body?.error ?? "unknown error"}`,
    );
  }
}
